import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Card,
    CardHeader,
    CardContent,
    Avatar,
    MenuItem,
    TextField,
    Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import AutoComplete from '@material-ui/lab/Autocomplete';
import NumberInput from '../../../../../common/components/UI/Input/Number';
import { UPDATE_VALIDATION_RULE } from '../../../store/ruleSet';
import { RequestTypes, DataType } from '../../../../Enums';
import StyledSelect from '../StyledSelect';
import InputTitle from '../InputTitle';
import StyledSwitch from '../StyledSwitch';
import './OnProbationSection.scss';

/**
 * @param {{ validationErrors: {[Key: string], isDisabled: boolean} }} props
 */
function OnProbationSection(props) {
    const { validationErrors, isDisabled, stepNumber, } = props;
    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'settings.ruleSet.employeeRulesStep.onProbationRule' });
    const dispatch = useDispatch();
    /** @type {EmploymentBillboard.AdminStore} */
    // @ts-ignore
    const { adminRuleSet, adminAppData } = useSelector((state) => state);
    const { requestType, validationRuleSet } = adminRuleSet;
    const { isOnProbation, probationLength,
        probationDataviewId, probationDataviewKeyAlias, probationCustomDateFormat, probationIsAllowToRequestOffers,
    } = validationRuleSet;
    const { dataviews, customDateFormat: customDateFormatList } = adminAppData;
    /**
    * @typedef {{value: string, label: string, disabled: boolean}} SelectDataviewFieldOption
    * @type {SelectDataviewFieldOption[]}
    */
    const probationDataviewFields = dataviews.find((x) => x.id === probationDataviewId)?.fields.map((x) => ({
        value: x.key,
        label: x.label,
    })) || [];

    const selectedProbationDataviewField = probationDataviewFields.find((x) => x.value === probationDataviewKeyAlias);
    const selectedProbationCustomDateFormat = customDateFormatList.find((x) => x.id === probationCustomDateFormat);
    const isToCover = requestType === RequestTypes.toCover;
    const isShiftSwap = requestType === RequestTypes.shiftSwap;

    const updateIsOnProbation = (value) => {
        let valuesToReset = null;
        if (!value) {
            valuesToReset = {
                probationLength: null,
                probationDataviewId: null,
                probationDataviewKeyAlias: '',
                probationCustomDateFormat: null,
                probationIsAllowToRequestOffers: false,
            }
        }
        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                isOnProbation: value,
                probationIsAllowToRequestOffers: isToCover,
                ...valuesToReset,
            },
        });
    }

    /**
     * @param {string} target
     * @param {string} value
     */
    const updateValidationRulesStore = (target, value) => {
        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                [target]: value,
            },
        });
    };

    return (
        <div className={`${isOnProbation ? 'ops-on-probation-section' : ''}`}>
            <StyledSwitch
                checked={isOnProbation}
                onChange={(value) => updateIsOnProbation(value)}
                label={t('title')}
                style={{ display: 'inline-flex' }}
                disabled={isDisabled}
            />
            {isOnProbation && (<>
                {(isToCover || isShiftSwap) && (
                    <StyledSwitch
                        checked={probationIsAllowToRequestOffers}
                        onChange={(value) => updateValidationRulesStore('probationIsAllowToRequestOffers', value)}
                        label={t('probationIsAllowToRequestOffersTitle')}
                        disabled={isDisabled}
                    />)}
                <div className="ops-day-input-container">
                    <Typography
                        variant="body1"
                        className="ops-numeric-input-unit-label"
                    >
                        {t('probationLengthLabelBeginning')}
                    </Typography>
                    <NumberInput
                        handleChangeInput={(value, target) => updateValidationRulesStore(target, value)}
                        min={1}
                        max={999}
                        step={1}
                        precision={0}
                        value={probationLength}
                        defaultValue={1}
                        target={'probationLength'}
                        isDisabled={isDisabled}
                        error={validationErrors.probationLength || (isOnProbation && probationLength && probationLength < 1)}
                    />
                    <Typography
                        variant="body1"
                        className="ops-numeric-input-unit-label"
                    >
                        {t('probationLengthLabelEnd')}
                    </Typography>
                    {
                        validationErrors.probationLength ? <span style={{ color: 'red', display: 'block' }}>{t('errors.probationLength')}</span> : null
                    }
                </div>
                <div className="ops-dataview-info-container">
                    <div className="rse--selection-field-container ops-one-third-space">
                        <InputTitle>{t('dataview')}</InputTitle>
                        <AutoComplete
                            id="dataview-select"
                            value={probationDataviewId !== null ? dataviews.find((x) => x.id === probationDataviewId) : null}
                            options={dataviews}
                            onChange={(_event, optionSelected) => updateValidationRulesStore('probationDataviewId', optionSelected?.id)}
                            renderInput={(params) => (<TextField
                                {...params}
                                variant="outlined"
                                placeholder="Select a dataview"
                            />)}
                            getOptionLabel={(option) => option.name}
                            includeInputInList
                            disabled={isDisabled}
                            disableClearable
                        />
                        {
                            validationErrors.probationDataviewId ? <span style={{ color: 'red' }}>{t('errors.probationDataviewId')}</span> : null
                        }
                    </div>
                    <div className="rse--selection-field-container ops-one-third-space">
                        <InputTitle>{t('dataviewField')}</InputTitle>
                        <StyledSelect
                            labelId="select-dataview-field-label"
                            id="select-dataview-field-type"
                            disabled={isDisabled}
                            renderValue={(/** @type {string} */
                                _value,
                            ) => selectedProbationDataviewField?.label}
                            value={probationDataviewKeyAlias !== null ? selectedProbationDataviewField : null}
                            onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                                event,
                            ) => updateValidationRulesStore('probationDataviewKeyAlias', event.target.value)}
                        >
                            {probationDataviewFields.map((x) => (
                                <MenuItem
                                    value={x.value}
                                    key={`SelectItem_${x.label}`}
                                >
                                    {x.label}
                                </MenuItem>
                            ))}
                        </StyledSelect>
                        {
                            validationErrors.probationDataviewKeyAlias ? <span style={{ color: 'red' }}>{t('errors.probationDataviewKeyAlias')}</span> : null
                        }
                    </div>
                    <div className="rse--selection-field-container ops-one-third-space">
                        <InputTitle>{t('dateFormat')}</InputTitle>
                        <StyledSelect
                            labelId="select-data-type-label"
                            id="select-data-type-type"
                            disabled={isDisabled}
                            renderValue={(/** @type {string} */
                                _value,
                            ) => selectedProbationCustomDateFormat?.name}
                            value={probationCustomDateFormat !== null ? selectedProbationCustomDateFormat : null}
                            onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                                event,
                            ) => updateValidationRulesStore('probationCustomDateFormat', event.target.value)}
                        >
                            {customDateFormatList.filter(x => x.dataType === DataType.Date).map((x) => (
                                <MenuItem
                                    value={x.id}
                                    key={`SelectItem_${x.name}`}
                                >
                                    {x.name}
                                </MenuItem>
                            ))}
                        </StyledSelect>
                        {
                            validationErrors.probationCustomDateFormat ? <span style={{ color: 'red' }}>{t('errors.probationCustomDateFormat')}</span> : null
                        }
                    </div>
                </div>
                
            </>)}
        </div>);
}

export default OnProbationSection;
